import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { RefreshCw } from "lucide-react";
import InnerLayout from "../../layouts/inner-layout";
import { api } from "../../../redux/apis/api";
import { url, fe_version } from "../../../redux/config";
import AddButton from "../../buttons/button";
import errorToast from "../../helper-components/error-toast";

const LOCAL_UPDATER_URL = "http://localhost:8005";
const HEALTH_POLL_INTERVAL_MS = 2000;
const HEALTH_POLL_TIMEOUT_MS = 2 * 60 * 1000;

const SoftwareUpdate = () => {
  const [version, setVersion] = useState(null);
  const [loadingVersion, setLoadingVersion] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const isUpdatingRef = useRef(false);
  const healthPollRef = useRef(null);

  useEffect(() => {
    const fetchVersion = async () => {
      setLoadingVersion(true);
      try {
        const response = await api.get("/get-version");
        if (
          response.data !== null &&
          typeof response.data === "object" &&
          !Array.isArray(response.data)
        ) {
          setVersion("Failed to fetch version number");
          errorToast("Failed to fetch current version");
        } else {
          setVersion(String(response.data).trim());
        }
      } catch (error) {
        errorToast("Failed to fetch current version");
      } finally {
        setLoadingVersion(false);
      }
    };
    fetchVersion();

    return () => {
      if (healthPollRef.current) {
        clearInterval(healthPollRef.current);
      }
    };
  }, []);

  const pollLocalHealth = () => {
    const startedAt = Date.now();
    healthPollRef.current = setInterval(async () => {
      // if (Date.now() - startedAt >= HEALTH_POLL_TIMEOUT_MS) {
      //   clearInterval(healthPollRef.current);
      //   healthPollRef.current = null;
      //   isUpdatingRef.current = false;
      //   setUpdating(false);
      //   setUpdateError("Cannot update the package");
      //   return;
      // }

      try {
        const response = await fetch(`${LOCAL_UPDATER_URL}/health`);
        const body = (await response.text())?.trim().toLowerCase();
        if (response.ok && body === "ok") {
          clearInterval(healthPollRef.current);
          healthPollRef.current = null;
          fetch(`${LOCAL_UPDATER_URL}/start`, { method: "POST" }).catch(() => {});
          window.location.href = `${LOCAL_UPDATER_URL}/`;
        }
      } catch (error) {
        // local service not up yet, keep polling
      }
    }, HEALTH_POLL_INTERVAL_MS);
  };

  const handleUpdate = async () => {
    if (isUpdatingRef.current) return;
    isUpdatingRef.current = true;
    setUpdateError(null);
    setUpdating(true);

    try {
      const response = await axios.post(`${url}/update`, {}
        , {
        timeout: 0,
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("accessToken"),
        },
      });
      if (response.status === 200) {
        pollLocalHealth();
      } else {
        isUpdatingRef.current = false;
        setUpdating(false);
        setUpdateError("Cannot update the package");
      }
    } catch (error) {
      isUpdatingRef.current = false;
      setUpdating(false);
      setUpdateError("Cannot update the package");
    }
  };

  return (
    <InnerLayout title="Software Update" backBtn={true}>
      <div className="h-full relative px-6 pt-6">
        {updating && (
          <div className="fixed inset-0 z-[1200] bg-transparent cursor-wait" />
        )}
        <div className="max-w-[520px] w-full bg-white border border-gray-200 rounded-xl px-7 py-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-primary w-fit px-2 py-2 rounded h-fit">
              <RefreshCw size={18} className="text-white" />
            </div>
            <div className="text-2xl font-bold text-gray-900">Software Update</div>
          </div>
          <div className="text-sm text-787878 mb-6">
            Check the current application version and update to the latest release.
          </div>

          <div className="flex items-center justify-between py-5 border-t border-gray-200">
            <div className="flex flex-col">
              <span className="text-xs text-gray-500">BE Version</span>
              <span className="font-medium text-base text-gray-900">
                {loadingVersion ? "Fetching version..." : version || "--"}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between py-5 border-t border-gray-200">
            <div className="flex flex-col">
              <span className="text-xs text-gray-500">FE Version</span>
              <span className="font-medium text-base text-gray-900">
                {fe_version || "--"}
              </span>
            </div>
            <AddButton
              onclkFn={handleUpdate}
              prefixIcon={false}
              text={updating ? "Updating..." : "Update"}
              type="button"
              isDisabled={loadingVersion || updating}
              loading={updating}
            />
          </div>

          {updateError && (
            <div className="text-sm text-[var(--remove-color)] font-medium mt-2">
              {updateError}
            </div>
          )}
        </div>
      </div>
    </InnerLayout>
  );
};

export default SoftwareUpdate;
